import React from 'react';
import { Box, Typography, Card, Button, Grid, Stack, Container } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import DnsIcon from '@mui/icons-material/Dns';
import EmailIcon from '@mui/icons-material/Email';
import PolicyIcon from '@mui/icons-material/Policy';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';

// Tool definitions for easy mapping (removed Domain Name Generator and Email Validator)
const tools = [
  {
    key: 'SPF Analyzer',
    name: 'SPF Analyzer',
    description: 'Analyze your SPF record and verify its configuration',
    href: '/tools/spf-analyzer',
    icon: <MarkEmailReadIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
  },
  {
    key: 'DKIM Analyzer',
    name: 'DKIM Analyzer',
    description: 'Verify your DKIM configuration and key rotation',
    href: '/tools/dkim-analyzer',
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
  },
  {
    key: 'DMARC Analyzer',
    name: 'DMARC Analyzer',
    description: 'Analyze your DMARC record and verify its configuration',
    href: '/tools/dmarc-analyzer',
    icon: <PolicyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
  },
  {
    key: 'MX Analyzer',
    name: 'MX Analyzer',
    description: 'Test your MX records and mail routing',
    href: '/tools/mx-analyzer',
    icon: <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
  },
  {
    key: 'DNS Super Tool',
    name: 'DNS Super Tool',
    description: 'Comprehensive DNS record analysis and validation',
    href: '/tools/dns-super-tool',
    icon: <DnsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
  },
];

/**
 * OtherToolsSection - Beautiful, reusable section for cross-promoting tools
 * @param {string} current - The name of the current tool to highlight
 */
const OtherToolsSection: React.FC<{ current: string }> = ({ current }) => {
  return (
    <Box sx={{ py: 6, bgcolor: '#f7fafd', mt: 8 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={800} textAlign="center" mb={4} color="primary">
          Try Our Other Tools
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {tools.map((tool) => {
            const isCurrent = tool.key === current;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tool.key}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: isCurrent ? 6 : 2,
                    border: isCurrent ? '2.5px solid #0B60B1' : '1.5px solid #e3e3e3',
                    bgcolor: isCurrent ? '#e3f0fc' : '#fff',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    transform: isCurrent ? 'scale(1.04)' : 'scale(1)',
                    ...(isCurrent ? {} : {
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.03)',
                        boxShadow: 5,
                      }
                    }),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 270,
                    p: 2,
                  }}
                  elevation={0}
                >
                  <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
                    {tool.icon}
                    <Typography variant="h6" fontWeight={700} align="center" color="primary.main">
                      {tool.name}
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary" sx={{ minHeight: 48 }}>
                      {tool.description}
                    </Typography>
                  </Stack>
                  {isCurrent ? (
                    <Button variant="contained" color="primary" disabled fullWidth sx={{ mt: 3, fontWeight: 700 }}>
                      You are here
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      href={tool.href}
                      fullWidth
                      sx={{ mt: 3, fontWeight: 700, borderWidth: 2 }}
                    >
                      Go to Tool
                    </Button>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default OtherToolsSection; 